import React from 'react';
import { differentiators } from '../../mock';
import { Users, Sparkles, Radar, Database, Target, ShieldCheck, Share2, Zap, ArrowUpRight } from 'lucide-react';

const iconMap = { Users, Sparkles, Radar, Database, Target, ShieldCheck, Share2 };

// Small stat ring (SVG progress arc)
function StatRing({ percent, color, label }) {
  const size = 72;
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
        />
      </svg>
      <span className="text-[10px] font-mono font-semibold" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

// Feature card graphic patterns per differentiator
function FeatureGraphic({ id, accent }) {
  if (id === '01') {
    // Team dots
    return (
      <div className="absolute top-4 right-4 flex -space-x-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="w-7 h-7 rounded-full border-2 border-[#0F1C40]" style={{ background: `linear-gradient(135deg, ${accent}, #4ECDC4)`, opacity: 1 - i * 0.15 }} />
        ))}
      </div>
    );
  }
  if (id === '02') {
    // rising bars
    return (
      <div className="absolute top-4 right-4 flex items-end gap-1 h-10">
        {[30, 45, 60, 78, 92].map((h, i) => (
          <div key={i} className="w-1.5 rounded-sm" style={{ height: `${h}%`, background: accent, opacity: 0.4 + i * 0.15 }} />
        ))}
      </div>
    );
  }
  if (id === '03') {
    // radar rings
    return (
      <svg className="absolute top-3 right-3" width="52" height="52" viewBox="0 0 52 52">
        {[8, 16, 24].map((r) => (
          <circle key={r} cx="26" cy="26" r={r} fill="none" stroke={accent} strokeOpacity="0.4" strokeDasharray="2 3" />
        ))}
        <circle cx="26" cy="26" r="3" fill={accent} />
        <circle cx="42" cy="20" r="2" fill={accent} />
        <circle cx="14" cy="30" r="1.5" fill={accent} />
      </svg>
    );
  }
  if (id === '04') {
    // stacked data cards
    return (
      <div className="absolute top-4 right-4 space-y-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-1.5 rounded-full" style={{ width: 40 - i * 8, background: accent, opacity: 0.8 - i * 0.15 }} />
        ))}
      </div>
    );
  }
  if (id === '05') {
    // ranked list
    return (
      <div className="absolute top-4 right-4 space-y-1 w-16">
        {[1, 2, 3].map((n, i) => (
          <div key={n} className="flex items-center gap-1.5">
            <span className="text-[9px] font-mono w-3 text-right" style={{ color: accent }}>{n}</span>
            <div className="h-1 flex-1 rounded-full" style={{ background: accent, opacity: 0.9 - i * 0.25 }} />
          </div>
        ))}
      </div>
    );
  }
  if (id === '06') {
    // shield check
    return (
      <div className="absolute top-4 right-4 flex items-center justify-center w-12 h-12 rounded-lg border" style={{ borderColor: `${accent}55`, background: `${accent}18` }}>
        <ShieldCheck className="w-6 h-6" style={{ color: accent }} strokeWidth={1.5} />
      </div>
    );
  }
  if (id === '07') {
    // channel dots connected
    return (
      <svg className="absolute top-3 right-3" width="60" height="52" viewBox="0 0 60 52">
        <line x1="10" y1="10" x2="50" y2="10" stroke={accent} strokeOpacity="0.4" strokeDasharray="2 3" />
        <line x1="50" y1="10" x2="30" y2="42" stroke={accent} strokeOpacity="0.4" strokeDasharray="2 3" />
        <line x1="30" y1="42" x2="10" y2="10" stroke={accent} strokeOpacity="0.4" strokeDasharray="2 3" />
        {[[10, 10], [50, 10], [30, 42]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="4" fill={accent} />
        ))}
      </svg>
    );
  }
  return null;
}

export default function Differentiators() {
  return (
    <section id="differentiators" className="relative py-24 lg:py-40 bg-[#0F1C40] text-[#F1EDE4] overflow-hidden">
      {/* atmospheric glow */}
      <div className="absolute top-40 right-1/4 w-[600px] h-[600px] rounded-full opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.5), transparent 70%)' }} />
      <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.6), transparent 70%)' }} />
      {/* grid overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(78,205,196,1) 1px, transparent 1px), linear-gradient(90deg, rgba(78,205,196,1) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* section head */}
        <div className="grid lg:grid-cols-12 gap-8 mb-16 lg:mb-24 items-end">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Why teams switch to us</span>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-[#4ECDC4] text-sm font-medium bg-[#4ECDC4]/10 border border-[#4ECDC4]/30 rounded-full px-4 py-2">
              <Zap className="w-4 h-4" />
              <span>Built for B2B. Powered by AI.</span>
            </div>
            <p className="text-white/60 text-[15px] leading-relaxed mt-6 max-w-sm">
              Seven capabilities working together — from raw intent data to booked meetings — so nothing gets lost between a signal and a sale.
            </p>
          </div>
          <div className="lg:col-span-7">
            <h2 className="text-5xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.98] font-serif italic">
              Demand gen built on
              <br />
              <span className="text-[#4ECDC4]">signal, not spray-and-pray.</span>
            </h2>
          </div>
        </div>

        {/* First 6 as bento grid, 07 spans wide with CTA */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {differentiators.slice(0, 6).map((d) => {
            const Icon = iconMap[d.icon];
            return (
              <div
                key={d.n}
                className="relative group bg-[#12234C] rounded-2xl p-7 lg:p-8 overflow-hidden hover:bg-[#173070] transition-all duration-500 reveal border border-white/5 hover:border-white/15"
              >
                {/* top gradient wash */}
                <div
                  className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-40 pointer-events-none group-hover:opacity-70 transition-opacity duration-500"
                  style={{ background: `radial-gradient(circle, ${d.accent}55, transparent 70%)` }}
                />

                {/* Feature graphic */}
                <FeatureGraphic id={d.n} accent={d.accent} />

                {/* Header */}
                <div className="relative flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: `${d.accent}44`, background: `${d.accent}18` }}>
                    <Icon className="w-5 h-5" style={{ color: d.accent }} strokeWidth={1.75} />
                  </div>
                  <span className="text-2xl font-serif italic tabular" style={{ color: d.accent }}>
                    {d.n}
                  </span>
                </div>

                {/* Title */}
                <h3 className="relative text-xl lg:text-2xl font-medium tracking-tight leading-snug mb-3 min-h-[3.5rem]">
                  {d.title}
                </h3>

                {/* Description */}
                <p className="relative text-[13px] text-white/60 leading-relaxed mb-6">{d.desc}</p>

                {/* Stat block */}
                <div className="relative pt-5 border-t border-white/10 flex items-center gap-4">
                  <StatRing
                    percent={Math.min(100, d.statNum <= 5 ? d.statNum * 20 : d.statNum)}
                    color={d.accent}
                    label={d.stat}
                  />
                  <p className="text-[11px] text-white/55 leading-relaxed flex-1">{d.statDesc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* 07: full-width feature block */}
        {(() => {
          const d = differentiators[6];
          const Icon = iconMap[d.icon];
          return (
            <div className="relative grid lg:grid-cols-12 gap-6 bg-[#12234C] rounded-2xl p-8 lg:p-12 overflow-hidden border border-white/5 hover:border-white/15 transition-all reveal">
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-40" style={{ background: `radial-gradient(circle, ${d.accent}55, transparent 70%)` }} />
              <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.4), transparent 70%)' }} />

              <div className="relative lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: `${d.accent}44`, background: `${d.accent}18` }}>
                    <Icon className="w-5 h-5" style={{ color: d.accent }} strokeWidth={1.75} />
                  </div>
                  <span className="text-2xl font-serif italic" style={{ color: d.accent }}>{d.n}</span>
                </div>
                <h3 className="text-3xl lg:text-5xl font-serif italic leading-tight mb-4">{d.title}</h3>
                <p className="text-white/70 text-lg leading-relaxed max-w-2xl">{d.desc}</p>

                {/* channel row */}
                <div className="flex flex-wrap gap-3 mt-8">
                  {['Paid', 'Email', 'Tele-calling', 'Content Syndication', 'LinkedIn'].map((c) => (
                    <span key={c} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 border border-white/10">{c}</span>
                  ))}
                </div>
              </div>

              <div className="relative lg:col-span-5 flex flex-col justify-between gap-6">
                <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <StatRing percent={83} color={d.accent} label={d.stat} />
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-0.5">Engagement uplift</div>
                      <div className="text-lg font-medium leading-snug">Multi-channel vs. single-channel</div>
                    </div>
                  </div>
                  <p className="text-xs text-white/55 leading-relaxed">{d.statDesc}</p>
                </div>

                <a href="#contact" className="inline-flex items-center gap-2 self-start bg-[#4ECDC4] text-[#0F1C40] pl-5 pr-4 py-3 rounded-full text-sm font-semibold hover:bg-white transition-colors">
                  Talk to us
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </a>
              </div>
            </div>
          );
        })()}
      </div>
    </section>
  );
}
